/**
 * Writing down the four things the records cannot tell you afterwards.
 *
 * `domain/auditLog.ts` derives most of the log from the database itself, which is both retroactive
 * and impossible to falsify. What derivation cannot see is: somebody signing in, somebody opening a
 * page, somebody DELETING a record — the evidence goes with it — and an access decision. Those are
 * written here, as they happen.
 *
 * THREE RULES THIS FILE KEEPS.
 *
 * 1. NEVER BLOCK, NEVER THROW. A log is bookkeeping, and a failed log write must not stop the act
 *    it was describing. Everything is fire-and-forget with the rejection swallowed — a manager
 *    whose keeper slate would not save because the audit row failed would be the worst possible
 *    trade of one thing for another.
 *
 * 2. NEVER LOG WHAT WAS READ, only that a page was opened. No document contents, no search terms,
 *    no IP, no user agent. The screen says "Ryan opened Rules", not what he was looking for.
 *
 * 3. COALESCE VIEWS. Ten managers on draft night would otherwise write a row per re-render. One
 *    row per person per page per half hour is enough to answer "was he in there", and keeps the
 *    log readable by a human, which is the only thing it is for.
 */

import { useEffect, useRef } from 'react';
import type { LeagueStore } from '../store/types.js';
import { auditEntry, type Note } from '../domain/auditEntry.js';

export type { Note };

const SEASON = 2026;

/** How long the same person may re-open the same page before it is worth another row. */
export const VIEW_COALESCE_MS = 30 * 60 * 1000;

/** Write one row. Returns immediately; failures are swallowed on purpose (rule 1 above). */
export function note(store: LeagueStore, actor: string, n: Note): void {
  if (!actor) return;
  void store.recordAudit?.(auditEntry(SEASON, actor, n)).catch(() => {});
}

/** Enough of a session to say who arrived, whether or not anybody has let him in. */
export interface Arrival {
  /** The GM id once an admin has paired the account. Empty for everybody else. */
  gm?: string;
  /** The Firebase uid. The only durable name an unpaired account has. */
  uid?: string;
  displayName?: string;
  email?: string;
  /** What the server worked out about the Yahoo account when it could not confirm a manager. */
  link?: { gm: string | null; confirmed: boolean; teamName: string | null; why: string;
    rejected?: boolean };
}

/**
 * One sign-in row per session, per account — INCLUDING the accounts nobody has approved.
 *
 * THIS USED TO MISS EXACTLY THE ARRIVALS WORTH SEEING (Tim, 22 August). It keyed on the GM id, and
 * an unpaired session carries `gm: ''` — so a new Yahoo account signing in and being told to ask an
 * admin wrote nothing at all, and Rajan signing in on two different Yahoo accounts left no trace
 * of either. An unrecognised arrival is the single most interesting line in an access log.
 *
 * So an unpaired account is logged under its `uid`, with whatever Yahoo said about it on the row:
 * the display name, the email, the team name the server found, and why it could not be matched.
 * Two accounts belonging to the same person are then two visibly different rows rather than none.
 *
 * `useEffect` on the identity rather than on mount: the shell renders before the session resolves,
 * so mounting is not signing in.
 */
export function useSignInAudit(store: LeagueStore, who: Arrival | null | undefined): void {
  const done = useRef<string | null>(null);
  const actor = who?.gm || who?.uid || null;
  useEffect(() => {
    if (!who || !actor || done.current === actor) return;
    done.current = actor;
    const paired = Boolean(who.gm);
    note(store, actor, {
      kind: 'sign-in',
      what: paired
        ? 'Signed in'
        : `Signed in — NOT paired to a manager${who.displayName ? ` (${who.displayName})` : ''}`,
      detail: {
        ...(paired ? {} : { Approved: 'no' }),
        'Yahoo name': who.displayName,
        Email: who.email,
        Account: who.uid,
        ...(who.link ? {
          'Team Yahoo reports': who.link.teamName ?? undefined,
          'Why not matched': who.link.why || undefined,
          Rejected: who.link.rejected ? 'yes' : undefined,
        } : {}),
      },
    });
  }, [store, actor, who, paired(who)]);
}

/** Stable dependency for the effect above — the object identity changes on every render. */
const paired = (who: Arrival | null | undefined) => Boolean(who?.gm);

/**
 * The act of looking at the league as somebody else, written down at both ends.
 *
 * Everything the app WRITES while this is on names the manager being impersonated, which is right
 * for the data and wrong for the log — a farm nomination Tim entered for John is John's
 * nomination, and the log said John made it. Some records carry a saver field and get it right on
 * their own; the ones that do not are rescued by overlapping them with these spans
 * (`attributeVia` in `domain/auditLog.ts`).
 *
 * A closed tab writes no stop, so a span can be left open. That is honest — the log says "Tim may
 * have been at the keyboard", which is the direction to err in.
 */
export function useActingAsAudit(
  store: LeagueStore, actor: string | null | undefined, actingAs: string | null | undefined,
): void {
  const current = useRef<string | null>(null);
  useEffect(() => {
    if (!actor) return;
    const next = actingAs && actingAs.toUpperCase() !== actor.toUpperCase()
      ? actingAs.toUpperCase() : null;
    if (current.current === next) return;
    if (current.current) {
      note(store, actor, {
        kind: 'acting-as',
        what: `Stopped acting as ${current.current}`,
        onBehalfOf: current.current,
        span: 'stop',
      });
    }
    if (next) {
      note(store, actor, {
        kind: 'acting-as',
        what: `Started acting as ${next}`,
        onBehalfOf: next,
        span: 'start',
        detail: { Note: 'Anything written while this is on is recorded under ' + next },
      });
    }
    current.current = next;
  }, [store, actor, actingAs]);
}

/**
 * A row when a page is opened, coalesced per person per page.
 *
 * The label is the route, not the content: `league/trades`, never which trade. Acting-as is
 * recorded on the row because "Tim, as Luke, opened Keepers" and "Luke opened Keepers" are
 * different events and the log would be lying if it printed them the same.
 */
export function useViewAudit(
  store: LeagueStore, actor: string | null | undefined, page: string, viewingAs?: string,
): void {
  const last = useRef(new Map<string, number>());
  useEffect(() => {
    if (!actor || !page) return;
    const key = `${actor}:${page}:${viewingAs ?? ''}`;
    const now = Date.now();
    const seen = last.current.get(key);
    if (seen !== undefined && now - seen < VIEW_COALESCE_MS) return;
    last.current.set(key, now);
    note(store, actor, {
      kind: 'view',
      what: `Opened ${page}`,
      target: page,
      ...(viewingAs && viewingAs.toUpperCase() !== actor.toUpperCase()
        ? { onBehalfOf: viewingAs } : {}),
    });
  }, [store, actor, page, viewingAs]);
}

/** Should this view be written down? Exported so the coalescing rule can be tested directly. */
export function shouldRecordView(
  seenAt: number | undefined, now: number, window = VIEW_COALESCE_MS,
): boolean {
  return seenAt === undefined || now - seenAt >= window;
}
